import React, { useCallback } from "react";
import { v4 as uuid } from "uuid";

import { usePlayContext } from "../../pages/Play/play-context";
import styles from "./action-lines.module.scss";
import { useActionPath } from "./hooks";
import { ArrowMarker } from "./markers";
import { ActionLineProps } from "./types";
import { getDribblePath } from "./utils";

export interface SquiggleLineProps extends ActionLineProps {
  size?: number;
  classicMarkerHead?: boolean;
}

export const SquiggleLine = ({
  action,
  controlPoints,
  strokeWidth = 0.3,
  size = 1,
  classicMarkerHead = false,
  animateable = false,
}: SquiggleLineProps) => {
  const { color = "#000" } = action;
  const markerEndId = uuid();

  const dribblePathGenerator = useCallback(
    (path: string, i: number, arr: string[]): string =>
      getDribblePath({
        pathLine: path,
        stepSize: size * 5,
        widthMultiplier: 1 / (size * 4),
        classicMarkerHead: classicMarkerHead || i !== arr.length - 1,
      }).dribblePath,
    [size, classicMarkerHead]
  );

  const { straightPathData, pathsWithScales, endTime } = useActionPath({
    action,
    controlPoints,
    pathGenerator: dribblePathGenerator,
  });
  const { animationTimerApi } = usePlayContext();
  const { effectiveTime: time } = animationTimerApi;

  return (
    <>
      <ArrowMarker id={markerEndId} color={color} xOffset={5.5} {...(classicMarkerHead && { xOffset: 2.5 })} />
      <path
        d={straightPathData}
        markerEnd={`url(#arrow-${markerEndId})`}
        stroke="none"
        fill="none"
        strokeWidth={strokeWidth}
        opacity={animateable && endTime && time >= endTime ? 0.2 : 1}
      />
      {pathsWithScales.map((path, i) => (
        <g key={i}>
          <path
            d={path.pathString}
            className={styles.actionLine}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`10000 10000`}
            strokeDashoffset={animateable ? path.scale(time) : 0}
          />
          <path
            d={path.pathString}
            className={styles.actionLine}
            stroke={color}
            strokeWidth={strokeWidth}
            opacity={0.2}
          />
        </g>
      ))}
    </>
  );
};
